// Plan 08-03 — loadTheme: bundled-active + lazy-alternate split.
//
// CONTEXT line 39 + 08-RESEARCH §Code Examples §Pattern 2:
//   - DEFAULT_THEME (court-of-runes) is "bundled active" — Vite serves
//     /themes/court-of-runes/manifest.json from public/ at build time, so
//     the first paint hits no network beyond the same-origin static asset.
//   - Alternates fetch via the content/loader.ts contract — Plan 04 wires
//     the actual themes:current KV pointer once R2 is in play; for now we
//     fetch directly from the public path (Vite dev / Pages build both
//     serve the manifests under the same /themes/ path).
//
// Validation: every loaded descriptor passes through isValidThemeDescriptor
// before being cached or returned. T-08-THEME-INJ mitigation — never apply
// an unverified descriptor.

import { createContentLoader } from '../../content/loader'
import {
  isValidThemeDescriptor,
  type ThemeDescriptor,
  type ThemeKey,
  ThemeManifestInvalidError,
} from './descriptor'

const themeLoader = createContentLoader<ThemeDescriptor>({
  cacheKeyPrefix: 'themes',
  buildPath: (subKey) => `/themes/${subKey}/manifest.json`,
  decode: (bytes) => {
    const text = new TextDecoder('utf-8').decode(bytes)
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch (err) {
      throw new ThemeManifestInvalidError(
        '<unknown>',
        `JSON parse failed: ${(err as Error).message}`,
      )
    }
    if (!isValidThemeDescriptor(parsed)) {
      const k =
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as { theme_key?: unknown }).theme_key === 'string'
          ? (parsed as { theme_key: string }).theme_key
          : '<unknown>'
      throw new ThemeManifestInvalidError(
        k,
        'descriptor failed isValidThemeDescriptor shape check',
      )
    }
    return parsed
  },
})

/**
 * Load the theme manifest for `themeKey`.
 *
 * Returns the validated `ThemeDescriptor`. Throws `ThemeManifestInvalidError`
 * if the loaded JSON does not conform to the schema (T-08-THEME-INJ).
 *
 * Tier-1 cached: repeat calls return the same descriptor reference.
 */
export async function loadTheme(themeKey: ThemeKey): Promise<ThemeDescriptor> {
  return themeLoader(themeKey)
}
